import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toJsDate } from '../../utils/formatDate';
import {
  subscribeToConversations,
  subscribeToMessages,
  sendMessage,
  markConversationRead
} from '../../services/messageService';

// Relative-time label for the conversation list (e.g. "5m", "3h", "2d") with a
// date fallback for anything older than a week.
const relativeTime = (value) => {
  const date = toJsDate(value);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString();
};

// Time-of-day label shown beneath each message bubble.
const messageTime = (value) => {
  const date = toJsDate(value);
  if (!date) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const Messages = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const currentName =
    userProfile?.displayName ||
    `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() ||
    currentUser?.email ||
    'Member';

  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(conversationId || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);

  // Subscribe to the user's conversation list.
  useEffect(() => {
    if (!currentUser) return undefined;
    const unsubscribe = subscribeToConversations(currentUser.uid, setConversations);
    return () => unsubscribe();
  }, [currentUser]);

  // Keep the selected conversation in sync with the URL param (auto-open a thread).
  useEffect(() => {
    if (conversationId) setSelectedId(conversationId);
  }, [conversationId]);

  // Subscribe to messages for the selected conversation; clean up on switch/unmount.
  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }
    const unsubscribe = subscribeToMessages(selectedId, setMessages);
    return () => unsubscribe();
  }, [selectedId]);

  // Opening a conversation (manual select or URL auto-open) clears its unread count.
  useEffect(() => {
    if (selectedId && currentUser) {
      markConversationRead(selectedId, currentUser.uid).catch((error) =>
        console.error('Error marking conversation read:', error)
      );
    }
  }, [selectedId, currentUser]);

  // Auto-scroll to the newest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Name of the other participant in a conversation (excludes the current user).
  const otherParticipantName = (conversation) => {
    if (!conversation) return 'Member';
    const names = conversation.participantNames || {};
    const otherUid = (conversation.participants || []).find((uid) => uid !== currentUser?.uid);
    return names[otherUid] || 'Member';
  };

  const selectedConversation = conversations.find((c) => c.id === selectedId) || null;

  const handleSelectConversation = (id) => {
    setSelectedId(id);
    navigate(`/messages/${id}`);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || !selectedId || sending) return;
    try {
      setSending(true);
      await sendMessage(selectedId, currentUser.uid, currentName, text);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Messages</h1>
          </div>
        </div>
      </div>

      {/* Messaging Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
            <div className="flex flex-col md:flex-row min-h-[32rem]">
              {/* Conversation List */}
              <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
                </div>
                <div className="flex-grow overflow-y-auto max-h-[28rem]">
                  {conversations.length > 0 ? (
                    conversations.map((conversation) => {
                      // Show the unread badge only when there are unread messages and
                      // the conversation isn't the currently-open one (which gets cleared).
                      const unreadCount =
                        selectedId === conversation.id
                          ? 0
                          : conversation.unread?.[currentUser?.uid] || 0;
                      return (
                        <button
                          key={conversation.id}
                          type="button"
                          onClick={() => handleSelectConversation(conversation.id)}
                          className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 focus:outline-none ${
                            selectedId === conversation.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-semibold text-gray-900 truncate">
                              {otherParticipantName(conversation)}
                            </span>
                            <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                              {relativeTime(conversation.lastMessageAt)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center gap-2">
                            <p className="text-sm text-gray-500 truncate">
                              {conversation.lastMessage || 'No messages yet'}
                            </p>
                            {unreadCount > 0 && (
                              <span className="flex-shrink-0 bg-apn-blue text-white text-xs font-semibold rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-6 text-center">
                      <p className="text-gray-500 text-sm">
                        No conversations yet. Start one from a member's profile.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Thread */}
              <div className="w-full md:w-2/3 flex flex-col">
                {selectedId ? (
                  <>
                    {/* Thread Header */}
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {otherParticipantName(selectedConversation)}
                      </h2>
                    </div>

                    {/* Message List */}
                    <div className="flex-grow overflow-y-auto p-4 space-y-3 max-h-[24rem]">
                      {messages.length > 0 ? (
                        messages.map((message) => {
                          const isMine = message.senderId === currentUser?.uid;
                          return (
                            <div
                              key={message.id}
                              className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-xs md:max-w-md px-4 py-2 rounded-lg ${
                                  isMine
                                    ? 'bg-apn-blue text-white'
                                    : 'bg-gray-100 text-gray-900'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                                <p
                                  className={`text-xs mt-1 ${
                                    isMine ? 'text-blue-100' : 'text-gray-400'
                                  }`}
                                >
                                  {messageTime(message.createdAt)}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-gray-500 text-sm">No messages yet. Say hello!</p>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Compose Box */}
                    <form onSubmit={handleSend} className="p-4 border-t border-gray-200">
                      <div className="flex items-end gap-2">
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSend(e);
                            }
                          }}
                          placeholder="Type a message..."
                          rows={2}
                          className="flex-grow px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue resize-none"
                        />
                        <button
                          type="submit"
                          disabled={sending || !newMessage.trim()}
                          className={`px-4 py-2 rounded-md text-white font-medium ${
                            sending || !newMessage.trim()
                              ? 'bg-gray-300 cursor-not-allowed'
                              : 'bg-apn-blue hover:bg-blue-700'
                          }`}
                        >
                          Send
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full p-12">
                    <p className="text-gray-500 text-center">
                      Select a conversation to start messaging.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Messages;
